import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useItalianCities } from '../../hooks/useItalianCities';
import _ from 'lodash';
import '../../styles/CityAutocomplete.css';

const CityAutocomplete = ({ 
  value, 
  onChange, 
  onCitySelect, 
  placeholder, 
  required = false,
  disabled = false,
  name = "CITY"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [hasSelected, setHasSelected] = useState(false); // Flag per indicare che l'utente ha selezionato
  const { cities, loading, searchCities } = useItalianCities();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => _.debounce((term) => {
      searchCities(term);
    }, 300),
    [searchCities]
  );

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    // Non fare ricerca se l'utente ha già selezionato una città
    if (hasSelected) {
      console.log('City already selected, skip search');
      return;
    }
    
    if (searchTerm.length >= 1) {
      console.log('Searching for:', searchTerm);
      debouncedSearch(searchTerm);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    
    // Cleanup
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch, hasSelected]);

  // Log when cities or loading state changes
  useEffect(() => {
    console.log('Cities updated:', cities, 'Loading:', loading);
  }, [cities, loading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setHasSelected(false); // Reset del flag quando l'utente modifica il campo
    setSearchTerm(newValue);
    
    // Call the parent onChange
    if (onChange) {
      onChange({
        target: {
          name,
          value: newValue.toUpperCase() // Salva in maiuscolo
        }
      });
    }
  };

  const handleCitySelect = (city) => {
    const cityUpperCase = city.city.toUpperCase(); // Converti in maiuscolo
    setSearchTerm(cityUpperCase);
    setHasSelected(true); // Marca che l'utente ha selezionato
    setIsOpen(false);
    
    // Passa i dettagli della città al parent - onCitySelect gestisce tutto incluso il campo CITY
    if (onCitySelect) {
      onCitySelect({
        city: cityUpperCase,
        province: city.province,
        postal_code: city.postal_code,
        region: city.region
      });
    }
    
    // NON chiamare onChange qui perché onCitySelect gestisce già l'aggiornamento completo
    // Se chiamiamo onChange, potremmo sovrascrivere provincia/cap/state appena impostati
  };

  return (
    <div className="city-autocomplete" ref={dropdownRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        name={name}
        autoComplete="off"
        style={{ width: '100%' }}
      />
      
      {isOpen && (
        <div 
          className="city-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderTop: 'none',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            borderRadius: '0 0 4px 4px'
          }}
        >
          {loading && (
            <div style={{ padding: '10px', textAlign: 'center' }}>
              Caricamento...
            </div>
          )}
          
          {!loading && cities.length === 0 && searchTerm.length >= 2 && (
            <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
              Nessuna città trovata
            </div>
          )}
          
          {!loading && cities.length > 0 && cities.map((city, index) => (
            <div
              key={index}
              onClick={() => handleCitySelect(city)}
              style={{
                padding: '10px',
                cursor: 'pointer',
                borderBottom: index < cities.length - 1 ? '1px solid #eee' : 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
              }}
            >
              <div style={{ fontWeight: 'bold' }}>{city.city}</div>
              {city.province && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Provincia: {city.province} - CAP: {city.postal_code}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;